import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronDown,
  IndianRupee,
  Layers,
  Calculator,
  Settings,
  ClipboardList,
  AlertTriangle,
  Info,
  CheckCircle2,
  XCircle,
  Wallet,
  FileText,
  GitBranch,
} from "lucide-react";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";

/* ------------------------------------------------------------------ */
/*  Data — sourced from actual project files                          */
/* ------------------------------------------------------------------ */

const COMPONENT_CATEGORIES = [
  { code: "earning", label: "Earning", desc: "Regular salary earnings paid to the employee", color: "ic-green" },
  { code: "benefit", label: "Benefit (Pre-tax)", desc: "Pre-tax benefits that reduce taxable income", color: "ic-blue" },
  { code: "deduction", label: "Deduction (Post-tax)", desc: "Post-tax deductions from employee salary", color: "ic-rose" },
  { code: "employer_contribution", label: "Employer Contribution", desc: "Employer-side contributions not deducted from employee pay", color: "ic-violet" },
  { code: "statutory", label: "Statutory", desc: "Government-mandated components calculated by the system engine", color: "ic-amber" },
];

const CALC_TYPES = [
  { code: "fixed", label: "Fixed / Manual", desc: "Set a flat amount per employee" },
  { code: "formula", label: "Formula", desc: "Compute from other components using an expression" },
  { code: "system", label: "System (PF/ESI/PT/TDS)", desc: "Calculated by the statutory engine automatically" },
];

const STANDARD_EARNINGS = [
  { code: "BASIC", name: "Basic", formula: "MONTHLY_CTC * 0.50" },
  { code: "HRA", name: "HRA", formula: "BASIC * 0.40" },
  { code: "CONVEYANCE", name: "Conveyance", formula: "1600" },
  { code: "MEDICAL", name: "Medical", formula: "1250" },
  { code: "SPECIAL", name: "Special Allowance", formula: "MONTHLY_CTC - (BASIC + HRA + CONVEYANCE + MEDICAL)" },
];

const STANDARD_DEDUCTIONS = [
  { code: "PF", name: "PF (Employee)", formula: "MIN(BASIC * 0.12, 1800)" },
  { code: "ESI", name: "ESI (Employee)", formula: "GROSS * 0.0075" },
  { code: "PT", name: "Professional Tax", formula: "200" },
  { code: "TDS", name: "TDS", formula: "0" },
  { code: "LOAN_RECOVERY", name: "Loan Recovery", formula: "0" },
];

const OTHER_DEDUCTION_CODES = [
  "NPS", "VPF", "INSURANCE", "LWF", "LOP", "ADVANCE",
];

const DERIVED_PRESETS = [
  { code: "MONTHLY_CTC", formula: "ANNUAL_CTC / 12" },
  { code: "BASIC", formula: "MONTHLY_CTC * 0.40" },
  { code: "HRA", formula: "BASIC * 0.40" },
  { code: "PF_EMPLOYEE", formula: "BASIC * 0.12" },
  { code: "PF_EMPLOYER", formula: "BASIC * 0.12" },
  { code: "SPA (Special Allowance)", formula: "MONTHLY_CTC - BASIC - HRA - PF_EMPLOYER" },
];

const FORMULA_TEMPLATES = [
  "Percentage", "Fixed Amount", "Conditional (if/then/else)",
  "Addition", "Subtraction", "Multiply", "Divide", "Advanced",
];

const FORMULA_VARIABLES = [
  "BASIC", "HRA", "CONVEYANCE", "MEDICAL", "SPECIAL",
  "GROSS", "CTC", "MONTHLY_CTC", "ANNUAL_CTC",
  "WORKING_DAYS", "PAYABLE_DAYS", "PAID_DAYS",
  "LOP_DAYS", "OT_HOURS", "TOTAL_DEDUCTIONS", "NET_PAY",
];

const FORMULA_FUNCTIONS = ["min", "max", "round", "abs", "floor", "ceil", "sqrt"];

const TEMPLATE_SOURCES = [
  { code: "fixed_amount", label: "Fixed Amount" },
  { code: "percentage", label: "Percentage" },
  { code: "component_formula", label: "Component Formula" },
  { code: "derived_variable", label: "Derived Variable" },
  { code: "statutory_engine", label: "Statutory Engine" },
  { code: "component_reference", label: "Component Reference" },
];

const SIMULATOR_FIELDS = [
  { field: "template_id", desc: "Select a salary template" },
  { field: "ctc", desc: "Annual CTC amount" },
  { field: "as_of_date", desc: "Date for the simulation" },
  { field: "employee_id", desc: "Optional — simulate for a specific employee" },
  { field: "tax_regime", desc: "New or Old tax regime" },
  { field: "location_state", desc: "2-letter state code for Professional Tax" },
];

const SIMULATOR_RESULTS = [
  "gross_monthly", "net_salary", "tds_monthly",
  "pf_employee", "esi_employee", "pt",
  "employer_cost_monthly", "annual_net",
];

const CTC_PRESETS = [
  { code: "standard_40", label: "Standard (40% Basic)" },
  { code: "compliance_50", label: "Compliance (50% Basic)" },
  { code: "startup_flexible", label: "Startup Flexible" },
];

const PF_SETTINGS_FIELDS = [
  { field: "wage_limit", desc: "PF wage ceiling (statutory: 25,000 from 17 Sep 2026; a higher value is honoured as a voluntary ceiling)" },
  { field: "is_capped", desc: "Whether PF is capped at wage limit" },
  { field: "employee_rate", desc: "Employee contribution rate (default: 12%)" },
  { field: "employer_rate", desc: "Employer contribution rate (default: 12%)" },
  { field: "eps_rate", desc: "EPS portion of employer PF (8.33%)" },
  { field: "epf_rate", desc: "EPF portion of employer PF (3.67%)" },
  { field: "eps_enabled", desc: "Whether EPS is enabled" },
  { field: "vpf_allowed", desc: "Whether Voluntary PF is allowed" },
];

const WIZARD_STEPS = [
  { num: "1", title: "Payroll Settings", desc: "Configure payroll cycle, payment day, working days, tax toggles, and extra work premiums" },
  { num: "2", title: "Salary Components", desc: "Select and configure earnings and deductions with calculation type and formulas" },
  { num: "3", title: "Formula Builder", desc: "Write and test formulas for each component, create derived variables" },
  { num: "4", title: "Salary Templates", desc: "Create and manage salary templates, add/remove components" },
  { num: "5", title: "Employee Allocation", desc: "Assign salary templates and CTC to employees (bulk or individual)" },
];

const V2_PRIMARY_TABS = [
  { label: "Payroll Settings", path: "/salary-v2/setup" },
  { label: "Salary Components", path: "/salary-v2/components" },
  { label: "Salary Structures", path: "/salary-v2/structures" },
  { label: "Pay Groups", path: "/salary-v2/pay-groups" },
];

const V2_ADVANCED_TABS = [
  "Formula Builder", "Derived Variables", "User Guide",
  "Benefits & Deductions", "Component Groups", "Scheduled Earnings",
  "Arrears & Corrections", "Statutory", "Simulator",
  "Dependencies", "Audit", "CTC Planner",
];

const SALARY_FAQS = [
  {
    q: "What is a salary component?",
    a: "A salary component is an individual building block of an employee's salary — such as Basic, HRA, PF, or TDS. Each component has a category (earning, deduction, benefit, employer contribution, or statutory), a calculation type (fixed, formula, or system), and optional properties like attendance proration and rounding rules.",
  },
  {
    q: "What are the standard earning components?",
    a: "BrixiGo auto-seeds five standard earnings: Basic (50% of Monthly CTC), HRA (40% of Basic), Conveyance (fixed 1,600), Medical (fixed 1,250), and Special Allowance (remainder after other earnings). These formulas can be customised per template.",
  },
  {
    q: "What are the standard deduction components?",
    a: "The standard deductions are: PF — MIN(Basic × 12%, 1800), ESI — Gross × 0.75%, Professional Tax — 200 (fixed, state-specific), TDS — 0 (configured separately), and Loan Recovery — 0. Additional deduction codes include NPS, VPF, Insurance, LWF, LOP, and Advance.",
  },
  {
    q: "What is a salary template?",
    a: "A salary template (also called a salary structure) groups salary components together and defines how each is calculated — fixed amount, percentage of another component, formula, derived variable, statutory engine, or component reference. Templates can be assigned to employees along with an Annual CTC.",
  },
  {
    q: "How do derived variables work?",
    a: "Derived variables are named formulas that compute values from other variables. BrixiGo provides standard Indian salary presets — MONTHLY_CTC (Annual CTC / 12), BASIC (40% of Monthly CTC), HRA (40% of Basic), PF_EMPLOYEE and PF_EMPLOYER (12% of Basic), and SPA (Special Allowance as the remainder). You can create custom derived variables with any supported expression.",
  },
  {
    q: "What operators and functions does the Formula Builder support?",
    a: "The formula engine supports arithmetic operators (+, −, ×, /), parentheses for grouping, percent literals (e.g. 40%), and functions: min, max, round, abs, floor, ceil, sqrt. Formulas can reference any component variable such as BASIC, GROSS, CTC, MONTHLY_CTC, WORKING_DAYS, LOP_DAYS, etc.",
  },
  {
    q: "What is the Salary Simulator?",
    a: "The Salary Simulator lets you preview how a salary template would calculate for a given Annual CTC, tax regime, and state. It shows gross monthly, net salary, TDS, PF, ESI, Professional Tax, employer cost, and annual net — without creating an actual payroll run.",
  },
  {
    q: "What is the CTC Planner?",
    a: "The CTC Planner is a standalone tool that breaks down an Annual CTC into earnings (Basic, HRA, Special Allowance), employee deductions (PF, ESI), and employer contributions (PF, ESI, Gratuity Accrual). It supports three presets: Standard (40% Basic), Compliance (50% Basic), and Startup Flexible.",
  },
  {
    q: "How do I assign a salary template to employees?",
    a: "Go to Employee Salary Structure. Select an employee, choose a salary template (only active templates are shown), enter the Annual CTC, set the effective date, and save. You can also use bulk allocation in the Payroll Setup Wizard (Step 5) to assign one template to many employees at once.",
  },
  {
    q: "What are component groups?",
    a: "Component groups let you organise related salary components together — for example, a PF_GROUP containing EMP_PF. Each group has a code, name, and description, and items within the group have a sequence number for ordering.",
  },
  {
    q: "How does salary configuration flow into payroll?",
    a: "The flow is: Salary Components → Formulas/Derived Variables → Salary Templates → Employee Assignment (Template + CTC) → Payroll Processing. The Payroll Setup Pipeline tracks seven steps: components, formulas, statutory, templates, template_components, assign_structure, and employees — all must be complete before running payroll.",
  },
  {
    q: "What is attendance proration?",
    a: "Salary components can be configured with an attendance proration mode. When enabled, the component amount is prorated based on payable days vs. working days. Variables like WORKING_DAYS, PAYABLE_DAYS, PAID_DAYS, and LOP_DAYS are available in formulas for this purpose.",
  },
];

const SALARY_ERRORS = [
  { code: "Code and name are required", cause: "Creating or editing a component without filling in the code and name fields", fix: "Enter both a component code (e.g. BASIC) and a display name before saving" },
  { code: "Select at least one component to continue to Formula Builder", cause: "Proceeding from the Components step in the setup wizard without selecting any components", fix: "Check at least one earning or deduction component before clicking Next" },
  { code: "Code, name, and expression are required", cause: "Saving a formula-type component without providing the formula expression", fix: "Enter the formula expression (e.g. BASIC * 0.40) along with code and name" },
  { code: "Template name is required", cause: "Trying to create a salary template without entering a name", fix: "Enter a template name in the create dialog before saving" },
  { code: "Delete failed — template may be assigned to employees", cause: "Deleting a salary template that is currently assigned to one or more employees", fix: "Reassign or remove the template from all employees before deleting" },
  { code: "Select a salary template first", cause: "Running the Salary Simulator without selecting a template", fix: "Choose a salary template from the dropdown before simulating" },
  { code: "Simulation failed", cause: "The simulator could not compute results — usually due to missing or circular formulas", fix: "Check that all components in the selected template have valid formulas and no circular dependencies" },
  { code: "Circular dependencies detected. Fix formulas before payroll run.", cause: "Component formulas reference each other in a loop (e.g. A depends on B, B depends on A)", fix: "Open the Dependency Graph page to view the cycle, then rewrite one of the circular formulas" },
  { code: "Enter a valid fixed amount", cause: "Template component set to fixed_amount but the amount field is empty or invalid", fix: "Enter a valid number in the amount field" },
  { code: "Enter a valid percentage", cause: "Template component set to percentage but the percentage field is empty or invalid", fix: "Enter a valid percentage value (e.g. 40)" },
  { code: "Select a reference", cause: "Template component set to component_reference but no reference component is selected", fix: "Choose the reference component from the dropdown" },
  { code: "Default salary structure name is required.", cause: "Payroll Settings step has an empty default structure name", fix: "Enter a name for the default salary structure in Payroll Settings" },
  { code: "Salary payment day must be between 1 and 31.", cause: "Invalid salary payment day in Payroll Settings", fix: "Enter a day between 1 and 31" },
];

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export default function SalaryHelpPage() {
  const [search, setSearch] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  const faqRef = useRef(null);
  const q = search.trim().toLowerCase();

  const filteredFaqs = q
    ? SALARY_FAQS.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q))
    : SALARY_FAQS;

  const filteredErrors = q
    ? SALARY_ERRORS.filter(
        (e) =>
          e.code.toLowerCase().includes(q) ||
          e.cause.toLowerCase().includes(q) ||
          e.fix.toLowerCase().includes(q)
      )
    : SALARY_ERRORS;

  return (
    <div className="brix-page">
      {/* ---- Hero ---- */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-amber">
              <IndianRupee size={28} color="#b45309" />
            </div>
            <div>
              <h1>Salary &amp; Components</h1>
              <p className="hs-hero-sub">
                Understand how salary components, templates, formulas, and salary
                configuration work in BrixiGo.
              </p>
            </div>
          </div>

          <form className="hs-search-wrap" onSubmit={(e) => { e.preventDefault(); if (search.trim() && faqRef.current) faqRef.current.scrollIntoView({ behavior: "smooth" }); }}>
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input
                type="text"
                className="hs-search-input"
                placeholder="Search salary help — e.g. formula, template, PF…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      <div>
        {/* ---- 1. Overview ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Layers size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Salary &amp; Components Overview</h2>
          </div>
          <div className="ah-content-card">
            <p>
              The Salary &amp; Components module lets you define every building
              block of employee compensation — earnings, deductions, benefits,
              employer contributions, and statutory components. Components are
              assembled into <strong>salary templates</strong> (structures),
              which are then assigned to employees along with an Annual CTC.
            </p>
            <p>
              The module is organised into four primary tabs and a collection of
              advanced tools:
            </p>
            <div className="ah-feature-grid">
              {V2_PRIMARY_TABS.map((t) => (
                <div className="ah-feature-item" key={t.label}>
                  <Settings size={16} color="#1a437d" /> {t.label}
                </div>
              ))}
            </div>
            <p style={{ marginTop: 14 }}>
              <strong>Advanced tools</strong> (available under the "More"
              dropdown):
            </p>
            <div className="ah-feature-grid">
              {V2_ADVANCED_TABS.map((t) => (
                <div className="ah-feature-item" key={t}>
                  <Settings size={14} color="#64748b" /> {t}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---- 2. Payroll Setup Wizard ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <ClipboardList size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Payroll Setup Wizard</h2>
          </div>
          <div className="ah-content-card">
            <p>
              First-time setup is guided by a 5-step wizard. On first open the
              system auto-seeds standard components and default formulas so you
              can start quickly. Only <strong>Admin</strong> and{" "}
              <strong>HR</strong> roles can edit settings.
            </p>
            <div className="ah-workflow-steps">
              {WIZARD_STEPS.map((s, i) => (
                <div className="ah-workflow-step" key={s.num}>
                  <div className="ah-workflow-num">{s.num}</div>
                  {i < WIZARD_STEPS.length - 1 && (
                    <div className="ah-workflow-connector" />
                  )}
                  <div className="ah-workflow-body">
                    <div className="ah-workflow-label">{s.title}</div>
                    <div className="ah-workflow-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---- 3. Component Categories & Calculation ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Layers size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Salary Components</h2>
          </div>
          <div className="ah-content-card">
            <p>
              A salary component is an individual line item in an employee's
              pay. Each component has a <strong>category</strong>, a{" "}
              <strong>calculation type</strong>, and optional properties like
              attendance proration mode and rounding rule.
            </p>

            <div className="ah-info-group">
              <div className="ah-info-heading">Component Categories</div>
              <div className="ah-status-grid">
                {COMPONENT_CATEGORIES.map((c) => (
                  <div className="ah-status-item" key={c.code}>
                    <div className="ah-status-header">
                      <span className={`ah-status-code ${c.color}`}>
                        {c.code.toUpperCase()}
                      </span>
                      <span className="ah-status-label">{c.label}</span>
                    </div>
                    <div className="ah-status-desc">{c.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="ah-info-group" style={{ marginTop: 18 }}>
              <div className="ah-info-heading">Calculation Types</div>
              <div className="ah-status-grid">
                {CALC_TYPES.map((c) => (
                  <div className="ah-status-item" key={c.code}>
                    <div className="ah-status-header">
                      <span className="ah-status-code ic-blue">{c.code.toUpperCase()}</span>
                      <span className="ah-status-label">{c.label}</span>
                    </div>
                    <div className="ah-status-desc">{c.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="ah-info-group" style={{ marginTop: 18 }}>
              <div className="ah-info-heading">Component Properties</div>
              <div className="ah-info-chips">
                {["code", "name", "description", "component_category", "calculation_type",
                  "formula_expression", "system_code", "is_active",
                  "rounding_rule", "attendance_proration_mode"].map((f) => (
                  <span className="ah-info-chip" key={f}>{f}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ---- 4. Standard Earnings ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <CheckCircle2 size={20} className="ah-section-icon" color="#15803d" />
            <h2>Standard Earnings</h2>
          </div>
          <div className="ah-content-card">
            <p>
              These five earning components are auto-seeded when you first open
              the Payroll Setup Wizard. Their default formulas can be customised.
            </p>
            <div className="ah-status-grid">
              {STANDARD_EARNINGS.map((e) => (
                <div className="ah-status-item" key={e.code}>
                  <div className="ah-status-header">
                    <span className="ah-status-code ic-green">{e.code}</span>
                    <span className="ah-status-label">{e.name}</span>
                  </div>
                  <div className="ah-status-desc">
                    Default formula: <code>{e.formula}</code>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---- 5. Standard Deductions ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <XCircle size={20} className="ah-section-icon" color="#e11d48" />
            <h2>Standard Deductions</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Five standard deductions are auto-seeded alongside earnings.
              Additional deduction codes are also recognised by the system.
            </p>
            <div className="ah-status-grid">
              {STANDARD_DEDUCTIONS.map((d) => (
                <div className="ah-status-item" key={d.code}>
                  <div className="ah-status-header">
                    <span className="ah-status-code ic-rose">{d.code}</span>
                    <span className="ah-status-label">{d.name}</span>
                  </div>
                  <div className="ah-status-desc">
                    Default formula: <code>{d.formula}</code>
                  </div>
                </div>
              ))}
            </div>
            <div className="ah-info-group" style={{ marginTop: 14 }}>
              <div className="ah-info-heading">Other Recognised Deduction Codes</div>
              <div className="ah-info-chips">
                {OTHER_DEDUCTION_CODES.map((c) => (
                  <span className="ah-info-chip" key={c}>{c}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ---- 6. Employer Contributions ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Wallet size={20} className="ah-section-icon" color="#7c3aed" />
            <h2>Employer Contributions</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Employer contributions are components paid by the employer and not
              deducted from the employee's take-home pay. They appear in the
              employer cost breakdown.
            </p>
            <div className="ah-info-group">
              <div className="ah-info-heading">Recognised Employer Contribution Codes</div>
              <div className="ah-info-chips">
                {["PF_EMPLOYER", "EMPLOYER_PF", "EPF_ER", "ESI_EMPLOYER", "EMPLOYER_ESI", "ESI_ER"].map((c) => (
                  <span className="ah-info-chip" key={c}>{c}</span>
                ))}
              </div>
            </div>
            <p style={{ marginTop: 10 }}>
              In derived variable presets, <strong>PF_EMPLOYER</strong> defaults
              to <code>BASIC * 0.12</code>.
            </p>
          </div>
        </div>

        {/* ---- 7. Salary Templates & Structures ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <FileText size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Salary Templates &amp; Structures</h2>
          </div>
          <div className="ah-content-card">
            <p>
              A salary template groups components together and defines how each
              is calculated. Templates have a <strong>name</strong>,{" "}
              <strong>description</strong>, and a status of{" "}
              <strong>active</strong>, <strong>draft</strong>, or{" "}
              <strong>inactive</strong>.
            </p>

            <div className="ah-info-group">
              <div className="ah-info-heading">Calculation Sources (per component in a template)</div>
              <div className="ah-status-grid">
                {TEMPLATE_SOURCES.map((s) => (
                  <div className="ah-status-item" key={s.code}>
                    <div className="ah-status-header">
                      <span className="ah-status-code ic-blue">{s.code.toUpperCase().replace(/_/g, " ")}</span>
                    </div>
                    <div className="ah-status-desc">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="ah-info-group" style={{ marginTop: 14 }}>
              <div className="ah-info-heading">Template Actions</div>
              <div className="ah-info-chips">
                {["Create", "Rename", "Duplicate", "Delete", "Change Status",
                  "Export JSON", "Import JSON", "Add/Remove Components"].map((a) => (
                  <span className="ah-info-chip" key={a}>{a}</span>
                ))}
              </div>
            </div>

            <p style={{ marginTop: 10 }}>
              Percentage-based components can reference <code>MONTHLY_CTC</code>,{" "}
              <code>CTC</code>, <code>BASIC</code>, or any other component in the
              catalog.
            </p>
          </div>
        </div>

        {/* ---- 8. Component Groups ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Layers size={20} className="ah-section-icon" color="#0f766e" />
            <h2>Component Groups</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Component groups let you organise related salary components
              together. Each group has a <strong>code</strong>,{" "}
              <strong>name</strong>, and <strong>description</strong>. Items
              within the group have a <strong>sequence</strong> number for
              ordering and a <strong>component_id</strong> linking to the actual
              component.
            </p>
          </div>
        </div>

        {/* ---- 9. Derived Variables ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <GitBranch size={20} className="ah-section-icon" color="#7c3aed" />
            <h2>Derived Variables</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Derived variables are named formulas that compute values from
              other variables. They are used in salary templates and component
              formulas. BrixiGo provides standard Indian salary presets that
              can be added with one click.
            </p>

            <div className="ah-info-group">
              <div className="ah-info-heading">Standard Indian Salary Presets</div>
              <div className="ah-status-grid">
                {DERIVED_PRESETS.map((p) => (
                  <div className="ah-status-item" key={p.code}>
                    <div className="ah-status-header">
                      <span className="ah-status-code ic-violet">{p.code}</span>
                    </div>
                    <div className="ah-status-desc">
                      <code>{p.formula}</code>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p style={{ marginTop: 12 }}>
              The derived variables page includes a live allocation preview with
              a KPI dashboard showing Monthly CTC input, allocated amount,
              remaining amount, and earnings count. It also shows allocation
              status: over-allocated (&gt;100.5%), fully allocated
              (&ge;99.5%), nearly full (&ge;90%), or healthy.
            </p>

            <div className="ah-info-group" style={{ marginTop: 12 }}>
              <div className="ah-info-heading">Other Recommended Variables</div>
              <div className="ah-info-chips">
                {["SPECIAL_ALLOWANCE", "MEDICAL_ALLOWANCE", "TRAVEL_ALLOWANCE",
                  "PERFORMANCE_ALLOWANCE", "FUEL_ALLOWANCE", "INTERNET_ALLOWANCE"].map((v) => (
                  <span className="ah-info-chip" key={v}>{v}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ---- 10. Formula Builder ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Calculator size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Formula Builder</h2>
          </div>
          <div className="ah-content-card">
            <p>
              The visual Formula Builder guides you through a 3-step process:
              Choose Formula Type, Build Formula, and Preview &amp; Validate.
            </p>

            <div className="ah-info-group">
              <div className="ah-info-heading">Formula Types</div>
              <div className="ah-info-chips">
                {["Earnings", "Allowance", "Deduction", "Employer Contribution", "Custom Formula"].map((t) => (
                  <span className="ah-info-chip" key={t}>{t}</span>
                ))}
              </div>
            </div>

            <div className="ah-info-group" style={{ marginTop: 12 }}>
              <div className="ah-info-heading">Formula Templates</div>
              <div className="ah-info-chips">
                {FORMULA_TEMPLATES.map((t) => (
                  <span className="ah-info-chip" key={t}>{t}</span>
                ))}
              </div>
            </div>

            <div className="ah-info-group" style={{ marginTop: 12 }}>
              <div className="ah-info-heading">Available Variables</div>
              <div className="ah-info-chips">
                {FORMULA_VARIABLES.map((v) => (
                  <span className="ah-info-chip" key={v}>{v}</span>
                ))}
              </div>
            </div>

            <div className="ah-info-group" style={{ marginTop: 12 }}>
              <div className="ah-info-heading">Supported Functions</div>
              <div className="ah-info-chips">
                {FORMULA_FUNCTIONS.map((f) => (
                  <span className="ah-info-chip" key={f}>{f}()</span>
                ))}
              </div>
            </div>

            <div className="ah-info-group" style={{ marginTop: 12 }}>
              <div className="ah-info-heading">Supported Operators</div>
              <div className="ah-info-chips">
                {["+", "−", "×", "/", "(  )", "%"].map((o) => (
                  <span className="ah-info-chip" key={o}>{o}</span>
                ))}
              </div>
            </div>

            <p style={{ marginTop: 12 }}>
              <strong>Popular examples:</strong> HRA = Basic × 40%, PF =
              Basic × 12%, Bonus = 5000, OT-based earnings.
            </p>
          </div>
        </div>

        {/* ---- 11. Salary Simulator ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Calculator size={20} className="ah-section-icon" color="#0f766e" />
            <h2>Salary Simulator</h2>
          </div>
          <div className="ah-content-card">
            <p>
              The Salary Simulator (formerly Salary Preview) lets you preview
              how a salary template calculates for a given CTC without creating
              an actual payroll run.
            </p>

            <div className="ah-manage-grid">
              <div className="ah-manage-card">
                <div className="ah-manage-title">
                  <Settings size={16} color="#1a437d" /> Input Fields
                </div>
                <ul className="ah-manage-list">
                  {SIMULATOR_FIELDS.map((f) => (
                    <li key={f.field}>
                      <strong>{f.field}</strong> — {f.desc}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="ah-manage-card">
                <div className="ah-manage-title">
                  <IndianRupee size={16} color="#15803d" /> Result Fields
                </div>
                <ul className="ah-manage-list">
                  {SIMULATOR_RESULTS.map((r) => (
                    <li key={r}>
                      <strong>{r}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* ---- 12. CTC Planner ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <IndianRupee size={20} className="ah-section-icon" color="#b45309" />
            <h2>CTC Planner</h2>
          </div>
          <div className="ah-content-card">
            <p>
              The CTC Planner is a standalone tool that breaks down an Annual
              CTC into earnings, employee deductions, and employer contributions.
            </p>

            <div className="ah-info-group">
              <div className="ah-info-heading">CTC Presets</div>
              <div className="ah-status-grid">
                {CTC_PRESETS.map((p) => (
                  <div className="ah-status-item" key={p.code}>
                    <div className="ah-status-header">
                      <span className="ah-status-code ic-amber">{p.code.toUpperCase().replace(/_/g, " ")}</span>
                    </div>
                    <div className="ah-status-desc">{p.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="ah-info-group" style={{ marginTop: 12 }}>
              <div className="ah-info-heading">Additional Options</div>
              <div className="ah-info-chips">
                {["Metro city", "Include gratuity accrual", "Basic % override", "HRA % override"].map((o) => (
                  <span className="ah-info-chip" key={o}>{o}</span>
                ))}
              </div>
            </div>

            <p style={{ marginTop: 10 }}>
              Results show: Earnings (Basic, HRA, Special Allowance), Employee
              deductions (PF, ESI), Employer contributions (PF, ESI, Gratuity
              Accrual), and calculated Gross and Net Take-Home amounts.
            </p>
          </div>
        </div>

        {/* ---- 13. Statutory Configuration ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Info size={20} className="ah-section-icon" color="#b45309" />
            <h2>Statutory Configuration (in Salary Module)</h2>
          </div>
          <div className="ah-content-card">
            <p>
              The Salary module includes a PF statutory configuration page
              where you can set up Provident Fund parameters for salary
              calculations.
            </p>

            <div className="ah-info-group">
              <div className="ah-info-heading">PF Configuration Fields</div>
              <div className="ah-status-grid">
                {PF_SETTINGS_FIELDS.map((f) => (
                  <div className="ah-status-item" key={f.field}>
                    <div className="ah-status-header">
                      <span className="ah-status-code ic-amber">{f.field.toUpperCase()}</span>
                    </div>
                    <div className="ah-status-desc">{f.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <p style={{ marginTop: 10 }}>
              System-type components (PF, ESI, PT, TDS) are calculated
              automatically by the statutory engine based on these
              configuration values.
            </p>
          </div>
        </div>

        {/* ---- 14. Employee Salary Assignment ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <ClipboardList size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Employee Salary Assignment</h2>
          </div>
          <div className="ah-content-card">
            <p>
              The Employee Salary Structure page lets you assign salary templates
              to employees. Only <strong>active</strong> templates are shown;
              draft and inactive templates are filtered out.
            </p>

            <div className="ah-manage-grid">
              <div className="ah-manage-card">
                <div className="ah-manage-title">
                  <Settings size={16} color="#1a437d" /> Assignment Fields
                </div>
                <ul className="ah-manage-list">
                  <li><strong>Employee</strong> — select from employee list</li>
                  <li><strong>Salary Template</strong> — active templates only</li>
                  <li><strong>Annual CTC</strong> — total cost to company</li>
                  <li><strong>Effective From</strong> — start date</li>
                </ul>
              </div>
              <div className="ah-manage-card">
                <div className="ah-manage-title">
                  <IndianRupee size={16} color="#15803d" /> Current Packages View
                </div>
                <ul className="ah-manage-list">
                  <li><strong>Employee</strong> — name</li>
                  <li><strong>Template</strong> — assigned template</li>
                  <li><strong>Annual CTC</strong> — total annual amount</li>
                  <li><strong>Monthly</strong> — CTC ÷ 12</li>
                  <li><strong>Effective From</strong> — start date</li>
                </ul>
              </div>
            </div>

            <p style={{ marginTop: 10 }}>
              Bulk allocation is also available through the Payroll Setup
              Wizard (Step 5), with two modes: "One Template → Many Employees"
              and "One Employee → One Template".
            </p>
          </div>
        </div>

        {/* ---- 15. Salary → Payroll Flow ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <GitBranch size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Salary &amp; Payroll Relationship</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Salary configuration feeds directly into payroll processing. The
              system tracks a 7-step payroll setup pipeline — all steps must be
              complete before running payroll.
            </p>
            <div className="ah-workflow-steps">
              {[
                { num: "1", title: "Components", desc: "Define salary components (earnings, deductions, statutory)" },
                { num: "2", title: "Formulas", desc: "Write and validate formulas for each component" },
                { num: "3", title: "Statutory", desc: "Configure PF, ESI, PT, TDS parameters" },
                { num: "4", title: "Templates", desc: "Create salary templates and add components" },
                { num: "5", title: "Template Components", desc: "Map components to templates with calculation sources" },
                { num: "6", title: "Assign Structures", desc: "Assign templates + CTC to employees" },
                { num: "7", title: "Employees", desc: "Ensure employees exist with required data" },
              ].map((s, i) => (
                <div className="ah-workflow-step" key={s.num}>
                  <div className="ah-workflow-num">{s.num}</div>
                  {i < 6 && <div className="ah-workflow-connector" />}
                  <div className="ah-workflow-body">
                    <div className="ah-workflow-label">{s.title}</div>
                    <div className="ah-workflow-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---- 16. FAQs ---- */}
        <div className="ah-section" ref={faqRef}>
          <div className="ah-section-header">
            <Info size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Frequently Asked Questions</h2>
          </div>

          {filteredFaqs.length === 0 && (
            <div className="ah-empty">No FAQs match your search.</div>
          )}

          <div className="hs-faq-list">
            {filteredFaqs.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <div className="hs-faq-item" key={i}>
                  <button
                    className="hs-faq-q"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    aria-expanded={isOpen}
                  >
                    <span>{faq.q}</span>
                    <motion.span
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                      style={{ display: "flex" }}
                    >
                      <ChevronDown size={18} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        className="hs-faq-a"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                      >
                        <p>{faq.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        {/* ---- 17. Common Errors ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <AlertTriangle size={20} className="ah-section-icon" color="#dc2626" />
            <h2>Common Salary Issues &amp; Solutions</h2>
          </div>

          {filteredErrors.length === 0 && (
            <div className="ah-empty">No issues match your search.</div>
          )}

          <div className="ah-errors-list">
            {filteredErrors.map((err, i) => (
              <div className="ah-error-item" key={i}>
                <div className="ah-error-title">
                  <AlertTriangle size={16} />
                  <code>{err.code}</code>
                </div>
                <div className="ah-error-row">
                  <span className="ah-error-label">Why:</span>
                  <span>{err.cause}</span>
                </div>
                <div className="ah-error-row">
                  <span className="ah-error-label">Fix:</span>
                  <span>{err.fix}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

